import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { log } from '../lib/logger';
import { Sentry, sentryEnabled } from '../instrument';

/**
 * Top-level React error boundary. Catches rendering / lifecycle errors in
 * any descendant component tree and presents a friendly fallback instead of
 * the default white screen. The error is forwarded to our logger so it
 * still shows up in dev tools / production error reporting.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    log.error('ErrorBoundary caught render error:', error, info?.componentStack);
    // Mirror to Sentry with the React component-stack attached as
    // ``contexts.react.componentStack`` so the issue page shows the
    // exact tree position. Wrapped in try/catch so a Sentry init
    // failure can't block our fallback UI.
    try {
      if (sentryEnabled()) {
        Sentry.withScope((scope) => {
          scope.setContext('react', { componentStack: info?.componentStack || null });
          scope.setTag('source', 'error-boundary');
          Sentry.captureException(error);
        });
      }
    } catch {
      // ignore
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-red-100 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#2F5D3A] mb-2">
            Something went sideways
          </h1>
          <p className="text-gray-600 mb-6">
            We hit an unexpected hiccup rendering this page. Your work isn&apos;t lost —
            try reloading, or head back to the home screen. Our team has been notified.
          </p>

          {this.state.error?.message && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6 font-mono text-left overflow-auto max-h-32">
              {String(this.state.error.message)}
            </div>
          )}

          <div className="flex justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] text-white font-medium hover:shadow-md transition-all"
              data-testid="error-boundary-reload"
            >
              <RefreshCw className="w-4 h-4" />
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              className="px-5 py-2.5 rounded-lg border border-[#4A7C59] text-[#4A7C59] font-medium hover:bg-[#4A7C59]/10 transition-all"
              data-testid="error-boundary-home"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
